import { Component, ViewChild } from '@angular/core';
import { ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';

import { ListsOverlayService } from '@app/lists/services/lists-overlay.service';
import { ListsPageData } from '@app/lists/model/lists-page.model';
import { ListsService } from '@app/lists/services/lists.service';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { NewSearchBasePage } from './shared/new-search-base.page';
import { NewSearchViewModel } from '@app/new-search/models/new-search-view.model';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { UserService } from '@app/user/services';
import { TranslateService } from '@ngx-translate/core';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { ResultList } from '@coveo/headless';
import { ProductService } from '@shared/services/product/product.service';
import { FacetFlyoutContainerComponent } from './components/facet-flyout-container/facet-flyout-container.component';
import { ChipTypeEnum } from '@shared/components/usf-chip/models/chip-type.enum';
import {
  TOP_SECTION_BACKGROUND_IMAGES_MAP,
  TOP_SECTION_BACKGROUND_POSITION_MAP,
  TOP_SECTION_IMAGE_TITLE_MAP,
  TOP_SECTION_TEXT_CONTENT_MAP,
  TOP_SECTION_TEXT_TITLE_MAP,
} from '@search/shared/models/search-page.model';
import {
  getFirstKey,
  mapFacets,
  topSectionFacetValueExist,
  topSectionMapConfig,
} from './utils/merchandising-banner.util';
import {
  ES_SEARCH_TERM,
  OUR_EXCLUSIVES_FACET,
  USF_PROGRAMS_FACET,
} from './constants/new-search-constants';
import { Params } from '@angular/router';
import { environment } from 'src/environments/environment';
import { RecipeDetailService } from '@app/mpp/pages/recipe-detail/services/recipe-detail.service';

@Component({
  selector: 'app-new-search',
  templateUrl: './new-search.page.html',
  styleUrls: ['./new-search.page.scss'],
})
export class NewSearchPage
  extends NewSearchBasePage
  implements ViewWillEnter, ViewWillLeave
{
  isAvendraUser: boolean;
  listPageData: ListsPageData;

  searchResult$: Subscription;

  resultsList: ResultList;
  queryParamUnsubscribe$: Subscription;

  readonly chipTypeEnum = ChipTypeEnum;

  @ViewChild(FacetFlyoutContainerComponent)
  facetFlyout: FacetFlyoutContainerComponent;

  isThreeOperationButtons = false;
  constructor(
    readonly platformService: PlatformService,
    readonly panAppState: PanAppState,
    public listsOverlayService: ListsOverlayService,
    public listsService: ListsService,
    readonly userService: UserService,
    public location: Location,
    public addToListModalService: AddToListModalService,
    readonly productService: ProductService,
    readonly recipeDetailService: RecipeDetailService,
  ) {
    super(
      listsOverlayService,
      addToListModalService,
      listsService,
      productService,
      location,
      recipeDetailService,
      userService,
    );
  }

  async ionViewWillEnter(): Promise<void> {
    const context = await this.coveoService.getContext();

    const path = this.location.path().split('?')[0];
    context.setView({
      url: `https://${environment.webDomain}${path}`,
    });

    this.buildListFacet();
    this.initializeCoveo({
      initializeSearchBoxController: this.isInitializeSearchBoxController(),
      pageType: 'search',
    });
    this.currentSearchTerm = '';
    this.newSearchViewModel = { products: [] } as NewSearchViewModel;

    const queryParam$ = this.route.queryParams.pipe(distinctUntilChanged());
    this.isTouchExperience$ = this.platformService.isTouchExperience$.subscribe(
      isTouchExperience => {
        this.isTouchExperience = isTouchExperience;
      },
    );

    this.listPageDataSub$ = this.listsService.listsView$().subscribe(data => {
      this.listPageData = data;
    });

    this.observeToggleState();
    this.panAppState.setPageTitle(PageTitle.shopProducts);

    this.scrollToTop();

    this.coveoClickEventSub$ =
      this.coveoService.searchResultClickEvent$?.subscribe(value => {
        const product = this.coveoSearch?.state?.products?.filter(
          v => v.ec_product_id === value?.productNumber?.toString(),
        );
        const coveoProduct = {
          ...product[0],
          ec_price: Number(value?.price),
        };
        this.coveoSearch
          .interactiveProduct({
            options: {
              product: coveoProduct,
            } as any,
          } as any)
          .select();
      });

    this.queryParamUnsubscribe$ = queryParam$.subscribe(params => {
      this.setUpParams(params);
      this.manageTopSection(params['facetFilters']);
      this.summary?.state?.firstRequestExecuted &&
        this.parameterManagerController?.synchronize(
          this.convertParamsToCoveo(params),
        );
      if (this.isAddToList) {
        this.selection = this.SelectionType.AddToList;
        this.addSelectionTypeToUrl();
      }
      if (this.isInventory) {
        this.selection = this.SelectionType.AddToInventory;
        this.addSelectionTypeToUrl();
      }
      if (this.isAddIngredient) {
        this.selection = this.SelectionType.AddIngredient;
        this.addSelectionTypeToUrl();
        this.backButtonText = 'i18n.newSearch.recipe';
        this.panAppState.setPageTitle(PageTitle.browseProducts);
      }
      if (this.originSearchPage) {
        this.setGoBackText();
      }
    });

    this.showInventory$ = this.userService
      .showMoxeInventory$()
      .subscribe(value => (this.showInventory = value));
    this.setMinWidthOperationButtons();
  }

  ionViewWillLeave(): void {
    this.searchResult$?.unsubscribe();
    this.isTouchExperience$?.unsubscribe();
    this.listPageDataSub$?.unsubscribe();
    this.showInventory = undefined;
    this.toggleStateSub$.unsubscribe();
    this.firstLoadPerformed = false;
    this.clearSearchSub$?.unsubscribe();
    this.isGuestUser$.unsubscribe();
    this.showInventory$?.unsubscribe();
    this.coveoTeardown();
    this.coveoClickEventSub$?.unsubscribe();
    this.currentSearchTerm = '';
    this.queryParamUnsubscribe$.unsubscribe();
    this.distinctSelectedCustomer$?.unsubscribe();
    this.coveoService.clearCustomContext();
  }

  searchAgain() {
    window.location.reload();
  }

  openFacetFlyout(facetId?: string) {
    this.facetFlyout.openFlyout(facetId);
  }

  onClearAll() {
    this.clearAllFacets();
  }

  notFoundEmitter() {
    this.searchAgain();
  }

  manageTopSection(facetFilters: string) {
    const facetMap = mapFacets(decodeURIComponent(facetFilters));
    if (this.currentSearchTerm === ES_SEARCH_TERM) {
      this.doUpdateTopSection(ES_SEARCH_TERM);
    } else if (facetMap && !this.currentSearchTerm) {
      const serveYou = topSectionFacetValueExist(facetMap, USF_PROGRAMS_FACET);
      const allScoop = topSectionFacetValueExist(
        facetMap,
        OUR_EXCLUSIVES_FACET,
      );
      // if we have both facets filters in the url
      if (serveYou && allScoop) {
        const firstFacet = getFirstKey(facetMap);
        const topSectionValue = topSectionMapConfig[firstFacet].topSectionValue;
        this.doUpdateTopSection(topSectionValue);
      } else {
        serveYou &&
          this.doUpdateTopSection(
            topSectionMapConfig[USF_PROGRAMS_FACET].topSectionValue,
          );
        allScoop &&
          this.doUpdateTopSection(
            topSectionMapConfig[OUR_EXCLUSIVES_FACET].topSectionValue,
          );
      }
    } else {
      this.isTopSection = false;
    }
  }

  doUpdateTopSection(text: string): void {
    this.isTopSection = true;
    this.topSectionBackgroundImageStyle =
      TOP_SECTION_BACKGROUND_IMAGES_MAP.get(text);
    this.topSectionBackgroundPosition =
      TOP_SECTION_BACKGROUND_POSITION_MAP.get(text);
    this.topSectionImageTitle = this.translateService.instant(
      TOP_SECTION_IMAGE_TITLE_MAP.get(text),
    );
    this.topSectionTextTitle = this.translateService.instant(
      TOP_SECTION_TEXT_TITLE_MAP.get(text),
    );
    this.topSectionTextContent = this.translateService.instant(
      TOP_SECTION_TEXT_CONTENT_MAP.get(text),
    );
    this.isTopSectionTextExpanded =
      this.platformService.getPlatformType() !== PlatformEnum.mobile;
  }

  setMinWidthOperationButtons() {
    const showAddToListButton = !this.isGuestUser && !this.isInventory;
    const showInventoryButton =
      this.showInventory && !this.isGuestUser && !this.isAddToList;
    this.isThreeOperationButtons = showAddToListButton && showInventoryButton;
  }

  isInitializeSearchBoxController(): boolean {
    const params: Params = this.navigationHelperService.getParamAsObject();
    return params?.addToList || params?.isInventory || params?.isAddIngredient;
  }
}
